import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Radio, Select, Space, Typography } from 'antd';
import React from 'react';
import { DropdownOption } from '../../core/models/DropdownOption';
import { errorWithUserMessage } from '../../helpers/NotificationHelper';
import { filterOptionForSearch } from '../../helpers/normalizeForSearch';
import { transformEnumToDropdownOptions } from '../../helpers/TransformHelper';
import { OfficialRoleEnum, OfficialVm, OfficialsClient } from '../../utils/api';

const { Text } = Typography;

export interface OfficialRolePair {
    officialId: number;
    roleId: OfficialRoleEnum;
}

/** merge = add to existing; replace = set officials to only this list */
export type BatchOfficialsApplyMode = 'merge' | 'replace';

interface Props {
    visible: boolean;
    onClose: () => void;
    selectedMatchCount: number;
    onApply: (pairs: OfficialRolePair[], mode: BatchOfficialsApplyMode) => Promise<void>;
}

interface State {
    rows: { key: string; officialId?: number; roleId?: OfficialRoleEnum }[];
    officials: OfficialVm[];
    loadingOfficials: boolean;
    applying: boolean;
    applyMode: BatchOfficialsApplyMode;
}

let rowKeySeq = 0;

const nextRowKey = (): string => `batch-off-${++rowKeySeq}`;

class BatchMatchOfficialsDrawer extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            rows: [{ key: nextRowKey() }],
            officials: [],
            loadingOfficials: false,
            applying: false,
            applyMode: 'merge',
        };
    }

    public componentDidMount = (): void => {
        if (this.props.visible) {
            void this.loadOfficials();
        }
    };

    public componentDidUpdate = (prevProps: Props): void => {
        if (this.props.visible && !prevProps.visible) {
            this.setState({
                rows: [{ key: nextRowKey() }],
                applying: false,
                applyMode: 'merge',
            });
            void this.loadOfficials();
        }
    };

    private loadOfficials = async (): Promise<void> => {
        this.setState({ loadingOfficials: true });
        try {
            const list = await new OfficialsClient().getAll();
            this.setState({
                officials: list || [],
                loadingOfficials: false,
            });
        } catch {
            this.setState({ loadingOfficials: false });
        }
    };

    private addRow = (): void => {
        this.setState((s) => ({
            rows: [...s.rows, { key: nextRowKey() }],
        }));
    };

    private removeRow = (key: string): void => {
        this.setState((s) => ({
            rows: s.rows.length <= 1 ? s.rows : s.rows.filter((r) => r.key !== key),
        }));
    };

    private setRowOfficial = (key: string, officialId: number | undefined): void => {
        this.setState((s) => ({
            rows: s.rows.map((r) =>
                r.key === key ? { ...r, officialId } : r
            ),
        }));
    };

    private setRowRole = (key: string, roleId: OfficialRoleEnum | undefined): void => {
        this.setState((s) => ({
            rows: s.rows.map((r) =>
                r.key === key ? { ...r, roleId } : r
            ),
        }));
    };

    private handleApply = async (): Promise<void> => {
        const { onApply, onClose } = this.props;
        const { rows, applyMode } = this.state;

        const pairs: OfficialRolePair[] = [];
        for (const row of rows) {
            if (
                row.officialId !== undefined &&
                row.roleId !== undefined
            ) {
                pairs.push({
                    officialId: row.officialId,
                    roleId: row.roleId,
                });
            }
        }

        if (pairs.length === 0) {
            errorWithUserMessage('Add at least one official with a role.');
            return;
        }

        this.setState({ applying: true });
        try {
            await onApply(pairs, applyMode);
            onClose();
        } finally {
            this.setState({ applying: false });
        }
    };

    public render(): React.ReactElement {
        const { onClose, selectedMatchCount } = this.props;
        const { rows, officials, loadingOfficials, applying, applyMode } = this.state;

        const roleOptions: DropdownOption[] = transformEnumToDropdownOptions(
            OfficialRoleEnum
        );

        const officialOptions = officials.map(
            (o: OfficialVm): { value: number; label: string } => ({
                value: o.id,
                label: `${o.name} ${o.surname}`,
            })
        );

        return (
            <div style={{ padding: '0 4px' }}>
                <Text type="secondary">
                    Choose officials and roles below, then apply to {selectedMatchCount} selected
                    match
                    {selectedMatchCount === 1 ? '' : 'es'}.
                </Text>
                <div style={{ marginTop: 16, marginBottom: 8 }}>
                    <Radio.Group
                        value={applyMode}
                        onChange={(e): void =>
                            this.setState({ applyMode: e.target.value as BatchOfficialsApplyMode })
                        }
                    >
                        <Radio value="merge">Add to existing officials</Radio>
                        <Radio value="replace">Replace all officials</Radio>
                    </Radio.Group>
                </div>
                {applyMode === 'merge' && (
                    <p style={{ marginBottom: 16, fontStyle: 'italic', color: '#595959' }}>
                        Current assignments stay; new pairs are added. Duplicate official+role is
                        skipped.
                    </p>
                )}
                {applyMode === 'replace' && (
                    <p style={{ marginBottom: 16, fontStyle: 'italic', color: '#595959' }}>
                        All current officials on each selected match are removed; only the
                        officials you list below will remain.
                    </p>
                )}
                <div style={{ marginTop: 4 }}>
                    {rows.map((row) => (
                        <Space
                            key={row.key}
                            style={{
                                display: 'flex',
                                marginBottom: 12,
                                width: '100%',
                                flexWrap: 'wrap',
                            }}
                            align="start"
                        >
                            <Select
                                showSearch
                                filterOption={filterOptionForSearch}
                                optionFilterProp="label"
                                placeholder="Official"
                                style={{ minWidth: 220, flex: 1 }}
                                loading={loadingOfficials}
                                allowClear
                                value={row.officialId}
                                options={officialOptions}
                                onChange={(v: number | undefined): void =>
                                    this.setRowOfficial(row.key, v)
                                }
                            />
                            <Select
                                placeholder="Role"
                                style={{ minWidth: 160 }}
                                allowClear
                                value={row.roleId}
                                options={roleOptions.map((r) => ({
                                    value: parseInt(r.id, 10) as OfficialRoleEnum,
                                    label: r.name,
                                }))}
                                onChange={(v: OfficialRoleEnum | undefined): void =>
                                    this.setRowRole(row.key, v)
                                }
                            />
                            <Button
                                type="text"
                                disabled={rows.length <= 1}
                                icon={<DeleteOutlined />}
                                aria-label="Remove row"
                                onClick={(): void => this.removeRow(row.key)}
                            />
                        </Space>
                    ))}
                </div>
                <Button
                    type="dashed"
                    onClick={this.addRow}
                    icon={<PlusOutlined />}
                    style={{ marginBottom: 24 }}
                >
                    Add another official
                </Button>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        type="primary"
                        loading={applying}
                        onClick={this.handleApply}
                    >
                        Apply to selected matches
                    </Button>
                </div>
            </div>
        );
    }
}

export default BatchMatchOfficialsDrawer;
